import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { logout } from "@/lib/api";
import { ShieldCheck, FileText, BookOpen,
  LayoutDashboard,
  Users,
  Building2,
  ListChecks,
  Mail,
  Briefcase,
  Linkedin,
  CreditCard,
  GraduationCap,
  LogOut,
  Send,
  KeyRound,
  PhoneCall,
} from "lucide-react";
import WishireLogo from "@/components/wishire/WishireLogo";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/companies", label: "Companies", icon: Building2 },
  { to: "/admin/recruiters", label: "Recruiter hubs", icon: Building2 },
  { to: "/admin/courses", label: "Courses", icon: BookOpen },
  { to: "/admin/agencies", label: "Agencies", icon: Briefcase },
  { to: "/admin/colleges", label: "Colleges", icon: GraduationCap },
  { to: "/admin/pricing", label: "Pricing", icon: CreditCard },
  { to: "/admin/jobs", label: "Jobs", icon: Briefcase },
  { to: "/admin/waitlist", label: "Waitlist", icon: ListChecks },
  { to: "/admin/leads", label: "LinkedIn leads", icon: Linkedin },
  { to: "/admin/contacts", label: "Contact forms", icon: Mail },
  { to: "/admin/sales", label: "Sales funnel", icon: Send },
  { to: "/admin/crm", label: "Lead CRM", icon: PhoneCall },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/audit", label: "Audit & AI spend", icon: ShieldCheck },
  { to: "/admin/integrations", label: "Integrations", icon: KeyRound },
];

export default function AdminLayout() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user && !user.is_admin) navigate("/app", { replace: true });
  }, [user, navigate]);

  if (!user) return null;
  if (!user.is_admin) return null;

  const handleLogout = async () => {
    try { await logout(); } catch (e) { /* ignore */ }
    setUser(null);
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0c0a09] text-stone-100 flex">
      <aside className={`${open ? "block" : "hidden"} md:block w-64 border-r border-stone-800 bg-[#1c1917] flex-col`}>
        <div className="p-6 border-b border-stone-800">
          <div className="font-display text-xl font-bold inline-flex items-baseline gap-1">
            <WishireLogo dark size="md" />
          </div>
          <p className="mt-1 text-xs text-stone-400 font-mono uppercase tracking-widest">Control room</p>
        </div>
        <nav className="p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-[#C2410C] text-white"
                    : "text-stone-300 hover:bg-stone-800/60 hover:text-white"
                }`
              }
            >
              <n.icon size={16} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto p-4 border-t border-stone-800">
          <div className="text-xs text-stone-400 font-mono uppercase tracking-widest mb-2">
            Signed in
          </div>
          <div className="text-sm">{user.email}</div>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="admin-logout"
            className="mt-3 inline-flex items-center gap-2 text-xs text-stone-300 hover:text-white"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-h-screen overflow-x-hidden">
        <div className="md:hidden p-4 border-b border-stone-800 flex items-center justify-between">
          <span className="font-display text-lg">Admin</span>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs px-3 py-1.5 rounded-full bg-stone-800"
          >
            Menu
          </button>
        </div>
        <div className="p-6 md:p-10 max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
